import React, { useState } from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { Icon } from 'components/Icon';
import { Modal, ModalContent, ModalHeader } from 'components/Modal';
import { H2, H3, Label, Text } from 'components/Typography';
import { theme, toRem } from 'design';
import {
  CalculatorSplitMethod,
  ICalculatorRule,
  IPoolData,
  IRemainderBeneficiary,
  IRemainderCondition,
  ITipCalculatorResultPool,
  ITipCalculatorResultReceived,
  RuleFundSource,
  ShareEligibilityMethod,
  TipCalculatorEligibilityMethodLabel,
  TipCalculatorReceivedDistributionType,
} from 'types';
import { centsToDollarsDisplay, Duration, getTipSources, toPercent, toTitleCase } from 'utils';
import { sumBy } from 'utils/arrays';

interface IPoolReportEmployeeProps {
  onClose: () => void;
  pool: IPoolData;
  jobTitle: string;
  businessDay: string;
}

const StyledModalHeader = styled(ModalHeader)`
  padding-bottom: ${toRem(16)};
  white-space: pre-wrap;
  word-break: break-word;
`;

const StyledModalContent = styled(ModalContent)`
  border-radius: ${toRem(8)};
  margin-top: ${toRem(8)};
`;

const ContentHeaderWrapper = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: space-around;
  width: 100%;
  margin-bottom: ${toRem(20)};
  flex-direction: column;

  @media (min-width: ${theme.breakpoint.small}px) {
    flex-direction: row;
  }
`;

const HeaderContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(4)};
  justify-items: center;
  width: 100%;

  > * {
    margin-bottom: 0;
  }
`;

const HeaderItem = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  margin-bottom: ${toRem(12)};

  label {
    font-size: ${toRem(13)};
    color: ${theme.color.neutral[500]};
    margin-bottom: ${toRem(6)};
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    margin-bottom: 0;

    label {
      font-size: ${toRem(12)};
      margin-bottom: ${toRem(4)};
    }
  }
`;

const PoolReportRowWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;

  padding: ${toRem(16)};
`;

const Employee = styled.div`
  width: 100%;
  align-items: center;
  display: flex;
  flex-direction: column;
  align-items: start;

  @media (min-width: ${theme.breakpoint.small}px) {
    display: grid;
    grid-template-columns: repeat(1, minmax(auto, 100%)) repeat(3, minmax(${toRem(80)}, 1fr));
    grid-gap: ${toRem(3)};
  }
`;

const EmployeeInfo = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin-bottom: ${toRem(13)};

  p.label {
    color: ${theme.color.neutral[500]};
  }

  p:nth-child(2) {
    display: flex;
    align-items: center;
    font-size: ${toRem(12)};
    color: ${theme.color.neutral[500]};
    font-weight: ${theme.font.weight.medium};
  }

  p > span {
    padding: ${toRem(2)} ${toRem(4)};
    background: ${theme.color.neutral[200]};
    border-radius: ${toRem(4)};
    margin-left: ${toRem(4)};
    color: ${theme.color.neutral[900]};
  }

  p > svg {
    margin-right: ${toRem(4)};
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    margin-bottom: 0;
  }
`;

const EmployeeTotal = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin-bottom: ${toRem(13)};

  label {
    font-size: ${toRem(12)};
    color: ${theme.color.neutral[500]};
    margin-bottom: ${toRem(4)};
  }

  :last-child {
    margin-bottom: 0;
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    margin-bottom: 0;
  }
`;

const CalculatorReportNavWrapper = styled.div`
  position: relative;
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  justify-content: flex-start;
  align-items: center;
  flex-flow: row nowrap;
  gap: ${toRem(4)};

  @media (min-width: ${theme.breakpoint.small}px) {
    margin-bottom: ${toRem(1)};
  }
`;

const CalculatorReportNavItem = styled.div`
  padding: ${toRem(8)} ${toRem(16)};
  font-size: ${toRem(13)};
  color: ${theme.color.neutral[600]};
  border-radius: ${theme.borderRadius.xsmall}px;
  font-weight: ${theme.font.weight.medium};

  &.active {
    background-color: ${theme.color.secondary[400]};
    color: #fff;
  }

  &:hover:not(.active) {
    cursor: pointer;
    background-color: ${theme.color.neutral[200]};
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    width: 100%;
    text-align: center;
    padding: ${toRem(6)} ${toRem(11.75)};
  }
`;

const PoolModalContentWrapper = styled.div`
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  background: ${theme.color.neutral[50]};
  width: 100%;
  border-radius: ${toRem(0)} ${toRem(0)} ${toRem(8)} ${toRem(8)};
  align-items: center;
  padding: ${toRem(16)};
  flex-flow: column nowrap;

  @media (min-width: ${theme.breakpoint.small}px) {
    border-radius: ${toRem(8)};
  }
`;

const PoolModalContentNav = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: ${toRem(16)};
  flex-direction: column;

  @media (min-width: ${theme.breakpoint.small}px) {
    flex-direction: row;
  }
`;

const StyledModalContentTitle = styled(Text)`
  font-size: ${toRem(14)};
  font-weight: ${theme.font.weight.semiBold};
  line-height: ${toRem(30.4)};
`;

const PoolModalTabContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  background: ${theme.color.white};
  border-radius: ${toRem(8)};

  > div:not(:last-child) {
    border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
  }
`;

const Rule = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;

  > div:last-child p {
    margin-top: ${toRem(6)};
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    display: grid;
    grid-template-columns: repeat(1, minmax(auto, 100%)) repeat(1, minmax(${toRem(55)}, 1fr));
    grid-gap: ${toRem(3)};
  }
`;

const RuleInfo = styled.div`
  display: flex;
  flex-flow: column nowrap;

  p.label {
    color: ${theme.color.neutral[500]};
    font-size: ${toRem(12)};
  }
`;

const HeaderDateSegmentWrapper = styled.div`
  display: flex;

  p:first-child {
    font-size: ${toRem(15)};
  }

  p:last-child {
    padding: ${toRem(3)} ${toRem(6)};
    background: ${theme.color.neutral[200]};
    border-radius: ${toRem(4)};
    font-size: ${toRem(11)};
    font-weight: ${theme.font.weight.medium};
    margin-left: ${toRem(4)};
  }
`;

const DisclaimerContainer = styled.div`
  width: 100%;
  background-color: ${theme.color.warning.light};
  padding: ${toRem(8)};
  border-radius: ${toRem(8)} ${toRem(8)} ${toRem(0)} ${toRem(0)};
  display: flex;
  flex-flow: column nowrap;
  border-top: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-left: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-right: ${toRem(1)} solid ${theme.color.neutral[200]};

  p,
  a {
    font-size: ${toRem(11)};
    font-weight: ${theme.font.weight.medium};
    text-align: center;
    text-decoration: none;
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    display: inherit;
    border-radius: ${toRem(8)};
    border: none;
    margin-bottom: ${toRem(16)};
  }
`;

const DisclaimerHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  width: 100%;

  @media (min-width: ${theme.breakpoint.small}px) {
    display: flex;
    flex-direction: column;
  }
`;

enum View {
  EMPLOYEES = 'employees',
  RULES = 'rules',
}

interface IJobReceivedFrom {
  distribution: TipCalculatorReceivedDistributionType;
  jobTitle: string;
  total: number;
  totalMinWorked: number;
  sources: string;
  category: string;
  split: CalculatorSplitMethod;
  eligibilityMethod: ShareEligibilityMethod;
  conditions?: IRemainderCondition[];
}

const findRemainderBeneficiary = (beneficiaries: IRemainderBeneficiary[], job: string) => beneficiaries?.find((b) => b.participants.some((participant) => participant.name.toLowerCase() === job.toLowerCase()));

const buildJobReceived = (job: string, currentPool: ITipCalculatorResultPool[], rules: ICalculatorRule[]): IJobReceivedFrom[] => {
  const receivedFrom: IJobReceivedFrom[] = [];

  currentPool.forEach((pool) => {
    pool.received?.forEach((received) => {
      const from = received.from.name.toLowerCase();
      const existing = receivedFrom.find((rf) => rf.jobTitle === from);

      if (existing) {
        existing.total += received.total;
      } else {
        const rule = rules.find((r) => (r.contributor && r.contributor.name.toLowerCase() === from) || r.additionalContributors?.some((c) => c.name.toLowerCase() === from));
        if (rule) {
          const remainder = findRemainderBeneficiary(rule.sharePolicy?.remainder?.beneficiaries, job);
          const shares = rule.sharePolicy?.shares?.find((s) => s.beneficiaries?.some((b) => b.name.toLowerCase() === job.toLowerCase()));

          const sources = getTipSources([...new Set([...(remainder?.sources || []), ...(shares?.sources || [])])]);

          receivedFrom.push({
            distribution: received.distribution,
            conditions: received.conditions,
            jobTitle: from,
            total: received.total,
            totalMinWorked: pool.totals.totalMinWorked,
            sources,
            category: received.category,
            split: received.split,
            eligibilityMethod: received.eligibilityMethod,
          });
        }
      }
    });
  });

  return receivedFrom;
};

const PoolModal = ({ onClose, pool, jobTitle, businessDay }: IPoolReportEmployeeProps) => {
  const employees = pool.result.employees.filter((emp) => emp.job.title.toLowerCase() === jobTitle.toLowerCase());
  const pools = pool.result.pools.filter((p) => p.job.title.trim().toLowerCase() === jobTitle.trim().toLowerCase());
  const rules = pool.result.rules.filter((r) => findRemainderBeneficiary(r.sharePolicy?.remainder?.beneficiaries, jobTitle) || r?.sharePolicy?.shares.find((s) => s.beneficiaries.some((b) => b.name.toLowerCase() === jobTitle)));

  const [view, setView] = useState<View>(View.EMPLOYEES);

  const totalTime: number = sumBy(pools, 'totals.totalMinWorked') || 0;

  // calculate received tips
  let totalReceivedTips: number = 0;
  let totalReceivedAutograts: number = 0;

  pools.forEach((p: ITipCalculatorResultPool) => {
    p.received?.forEach((r: ITipCalculatorResultReceived) => {
      r.sources.forEach((s) => {
        if (s.source === RuleFundSource.TIPS) {
          totalReceivedTips += s.total || 0;
        } else if (s.source === RuleFundSource.AUTO_GRATUITIES) {
          totalReceivedAutograts += s.total || 0;
        }
      });
    });
  });

  totalReceivedTips = Math.trunc(totalReceivedTips);
  totalReceivedAutograts = Math.trunc(totalReceivedAutograts);
  const total: number = totalReceivedTips + totalReceivedAutograts;

  // build the received from array
  const receivedFrom = buildJobReceived(jobTitle, pools, rules);

  // get the share as a percent for the contributing job from the rules
  const getSharePercent = (contributingJob: string, job: string): string => {
    const rule = pool.result.rules.find((r) => r.contributor && r.contributor.name.toLowerCase() === contributingJob.toLowerCase());
    const share = rule?.sharePolicy?.shares?.find((share) => share.beneficiaries.map((j) => j.name.toLowerCase()).includes(job))?.share;

    if (share) return toPercent(share);
    return null;
  };

  return (
    <Modal data-cy="pool-report-modal" isActive onClose={onClose} maxWidth={640}>
      <StyledModalHeader>
        <HeaderContent>
          <H2 data-cy="modal-job-header" fontWeight="semibold">
            {toTitleCase(jobTitle)} Pool
          </H2>
          <HeaderDateSegmentWrapper>
            <Text data-cy="modal-date-header" fontWeight="medium">
              {dayjs(businessDay).format('MMMM Do, YYYY')}
            </Text>
            <Text data-cy="modal-segment-header">{pool.daySegment}</Text>
          </HeaderDateSegmentWrapper>
        </HeaderContent>
      </StyledModalHeader>
      <StyledModalContent>
        <DisclaimerHeaderWrapper>
          <DisclaimerContainer>
            <Text>These amounts do not include retained tips.</Text>
          </DisclaimerContainer>
          <ContentHeaderWrapper>
            <HeaderItem>
              <Label>Time Worked</Label>
              <H3 data-cy="modal-content-total-time">{new Duration(totalTime).toString()}</H3>
            </HeaderItem>
            <HeaderItem>
              <Label>Received Tips</Label>
              <H3 data-cy="modal-content-total-tips-received">{centsToDollarsDisplay(totalReceivedTips)}</H3>
            </HeaderItem>
            <HeaderItem>
              <Label>Received Autograts</Label>
              <H3 data-cy="modal-content-total-autograts-received">{centsToDollarsDisplay(totalReceivedAutograts)}</H3>
            </HeaderItem>
            <HeaderItem>
              <Label>Total</Label>
              <H3 data-cy="modal-content-total">{centsToDollarsDisplay(total)}</H3>
            </HeaderItem>
          </ContentHeaderWrapper>
        </DisclaimerHeaderWrapper>

        <PoolModalContentWrapper>
          <PoolModalContentNav>
            <StyledModalContentTitle>Pool Details</StyledModalContentTitle>

            <CalculatorReportNavWrapper data-cy="pool-report-modal-nav">
              <CalculatorReportNavItem onClick={() => setView(View.EMPLOYEES)} {...(view === View.EMPLOYEES && { className: 'active' })} data-cy="pool-report-modal-nav-item">
                Employees
              </CalculatorReportNavItem>
              <CalculatorReportNavItem onClick={() => setView(View.RULES)} {...(view === View.RULES && { className: 'active' })} data-cy="pool-report-modal-nav-item">
                Rules
              </CalculatorReportNavItem>
            </CalculatorReportNavWrapper>
          </PoolModalContentNav>
          <PoolModalTabContent data-cy="pool-report-modal-content">
            {view === View.EMPLOYEES && (
              <>
                {employees.map((emp, index) => {
                  let empTotalTips: number = 0;
                  let empTotalAutograts: number = 0;

                  emp.received.forEach((r: ITipCalculatorResultReceived) => {
                    r.sources.forEach((s: any) => {
                      if (s.source === RuleFundSource.TIPS) {
                        empTotalTips += s.total || 0;
                      }

                      if (s.source === RuleFundSource.AUTO_GRATUITIES) {
                        empTotalAutograts += s.total || 0;
                      }
                    });
                  });

                  const empTotal = empTotalTips + empTotalAutograts;
                  const empPercentWorked = total === 0 ? '0%' : toPercent(empTotal / total, 1);

                  return (
                    <PoolReportRowWrapper key={index}>
                      <Employee data-cy="pool-report-employees-row">
                        <EmployeeInfo>
                          <Text data-cy="employee-name" fontWeight="semibold" size="small">
                            {emp.name}
                          </Text>
                          <Text data-cy="employee-time" size="small" className="label">
                            <Icon icon="Time" /> {new Duration(emp.totals.totalMinWorked).toString()} <span>{empPercentWorked}</span>
                          </Text>
                        </EmployeeInfo>
                        <EmployeeTotal>
                          <Label>Tips</Label>
                          <Text data-cy="employee-tip" size="small">
                            {centsToDollarsDisplay(empTotalTips)}
                          </Text>
                        </EmployeeTotal>
                        <EmployeeTotal>
                          <Label>Autograts</Label>
                          <Text data-cy="employee-autograts" size="small">
                            {centsToDollarsDisplay(empTotalAutograts)}
                          </Text>
                        </EmployeeTotal>
                        <EmployeeTotal>
                          <Label>Total</Label>
                          <Text data-cy="employee-total" fontWeight="semibold" size="small">
                            {centsToDollarsDisplay(empTotal)}
                          </Text>
                        </EmployeeTotal>
                      </Employee>
                    </PoolReportRowWrapper>
                  );
                })}
              </>
            )}

            {view === View.RULES && (
              <>
                {receivedFrom.map((rf, index) => (
                  <PoolReportRowWrapper key={index}>
                    {rf.distribution === TipCalculatorReceivedDistributionType.SHARE && (
                      <Rule data-cy="pool-report-rule-row">
                        <RuleInfo>
                          <Text data-cy="rule-info" fontWeight="semibold" size="small">
                            {toTitleCase(rf.jobTitle)} shared {getSharePercent(rf.jobTitle, jobTitle)} of {rf.category ? `${rf.category} Sales` : rf.sources}
                          </Text>
                          <Text data-cy="rule-share-mode" size="small" className="label">
                            per {TipCalculatorEligibilityMethodLabel[rf.eligibilityMethod]} split by {rf.split.toLowerCase().replace('_', ' ')}
                          </Text>
                        </RuleInfo>
                        <div>
                          <Text data-cy="rule-total" fontWeight="semibold" size="small">
                            {centsToDollarsDisplay(rf.total)}
                          </Text>
                        </div>
                      </Rule>
                    )}
                    {rf.distribution === TipCalculatorReceivedDistributionType.REMAINDER && (
                      <Rule data-cy="pool-report-rule-row">
                        <RuleInfo>
                          <Text data-cy="rule-info" fontWeight="semibold" size="small">
                            {toTitleCase(rf.jobTitle)} earning {rf.conditions && rf.conditions[0]?.hours ? `${rf.conditions[0].hours} hour${rf.conditions[0].hours !== 1 ? 's' : ''} of ` : ''} {rf.sources}
                          </Text>
                          <Text data-cy="rule-share-mode" size="small" className="label">
                            split by {rf.split.toLowerCase().replace('_', ' ')}
                          </Text>
                        </RuleInfo>
                        <div>
                          <Text data-cy="rule-total" fontWeight="semibold" size="small">
                            {centsToDollarsDisplay(rf.total)}
                          </Text>
                        </div>
                      </Rule>
                    )}
                  </PoolReportRowWrapper>
                ))}
              </>
            )}
          </PoolModalTabContent>
        </PoolModalContentWrapper>
      </StyledModalContent>
    </Modal>
  );
};

export default PoolModal;
